import { useQuery, useQueryClient } from "@tanstack/react-query";
import { ExceptionsQueryParams } from "../../api/query.types";
import { ExceptionsResponseDTO } from "../../api/response.types";

import { exceptionsService } from "../../api/api";
import { ExceptionsModel } from "../model";

type ResetSearchQueryFunc = () => Promise<void>;

export type UseQueryReturnType = {
  resetExceptionsQuery: ResetSearchQueryFunc;
  removeExceptionsCache: () => void;
  cancelExceptionsRequest: () => Promise<void>;
  exceptionsResponse: ExceptionsModel | undefined;
  isExceptionsSuccess: boolean;
  isExceptionsError: boolean;
  isExceptionsLoading: boolean;
  isExceptionsFetching: boolean;
};

type UseQueryParams = {
  shouldRequestOnMount?: boolean;
  params: ExceptionsQueryParams;
};

const EXCEPTIONS_QUERY_KEY = "get_exceptions_query";

export const useExceptionsQuery = ({ shouldRequestOnMount = true, params }: UseQueryParams): UseQueryReturnType => {
  const queryClient = useQueryClient();

  const { data, isError, isLoading, isSuccess, isFetching } = useQuery<ExceptionsResponseDTO, Error, ExceptionsModel>({
    queryKey: [`${EXCEPTIONS_QUERY_KEY}-${params.number}`],
    queryFn: ({ signal }) => exceptionsService.search(params, signal),
    select: (response) => new ExceptionsModel(response),
    enabled: params && shouldRequestOnMount,
  });

  const removeExceptionsCache = () => {
    queryClient.removeQueries({ queryKey: [EXCEPTIONS_QUERY_KEY] });
  };

  const resetExceptionsQuery: ResetSearchQueryFunc = () => {
    return queryClient.resetQueries({ queryKey: [EXCEPTIONS_QUERY_KEY] });
  };

  const cancelExceptionsRequest = () => {
    return queryClient.resetQueries({ queryKey: [EXCEPTIONS_QUERY_KEY] });
  };

  return {
    resetExceptionsQuery,
    removeExceptionsCache: removeExceptionsCache,
    exceptionsResponse: data,
    isExceptionsSuccess: isSuccess,
    isExceptionsError: isError,
    isExceptionsLoading: isLoading,
    isExceptionsFetching: isFetching,
    cancelExceptionsRequest: cancelExceptionsRequest,
  };
};
